"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Smartphone,
  Brain,
  Package,
  FileSpreadsheet,
  CalendarCheck,
  BadgeDollarSign,
  X,
} from "lucide-react";
import { Container } from "@/components/shared/container";
import { AnimateOnScroll } from "@/components/shared/animate-on-scroll";
import { Badge } from "@/components/ui/badge";
import { DemoButton } from "@/components/demo-button";
import { VisualDevices } from "./visuals/visual-devices";
import { VisualPricing } from "./visuals/visual-pricing";
import { VisualTeam } from "./visuals/visual-team";

export type BentoTile = {
  id: string;
  label: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  bullets: string[];
  size: "lg" | "md" | "sm";
  accent?: "primary" | "neutral";
  visual?: React.ReactNode;
};

export const TILES: BentoTile[] = [
  {
    id: "all-included",
    label: "Tylko w Vetinie",
    title: "Wszystko w jednej cenie, bez dopłat",
    description:
      "Aktualizacje, integracje, migracja danych i rezerwacja online — wszystko wliczone w cenę. Bez ukrytych kosztów, bez niespodzianek na fakturze.",
    icon: BadgeDollarSign,
    size: "lg",
    accent: "primary",
    bullets: [
      "Automatyczne aktualizacje wliczone",
      "Vetina Bridge — integracje z laboratoriami i aparaturą wliczone",
      "Vetina Booking — rezerwacja wizyt online wliczona",
      "Bezpłatna migracja danych z każdego systemu",
    ],
  },
  {
    id: "ai-prep",
    label: "Tylko w Vetinie",
    title: "AI, które przygotowuje Cię do wizyty",
    description:
      "Zamiast przewijać dziesiątki wpisów, dostajesz gotowe podsumowanie AI — kluczowe diagnozy, terapie, istotne uwagi.",
    icon: Brain,
    size: "md",
    accent: "primary",
    bullets: [
      "AI podsumowanie historii pacjenta",
      "Automatyczna Książka Leczenia Zwierząt",
      "Generowanie raportów jednym kliknięciem",
    ],
  },
  {
    id: "inventory",
    label: "Tylko w Vetinie",
    title: "Inwentaryzacja bez zamykania placówki",
    description:
      "Licz stan magazynu, podczas gdy w gabinecie normalnie toczą się wizyty. System sam uwzględnia rozchód w czasie rzeczywistym.",
    icon: Package,
    size: "md",
    accent: "primary",
    bullets: [
      "Inwentaryzacja w trakcie pracy placówki",
      "Automatyczne rozbijanie opakowań",
      "Import faktur z dowolnego pliku XML",
    ],
  },
  {
    id: "any-device",
    label: "Codziennie",
    title: "Działa na każdym urządzeniu",
    description:
      "Przeglądarka wystarczy. Laptop, tablet przy stole zabiegowym, telefon w terenie.",
    icon: Smartphone,
    size: "sm",
    bullets: [
      "Praca w przeglądarce — bez instalacji",
      "Synchronizacja w czasie rzeczywistym",
      "Działa na iOS, Android, Windows, macOS",
    ],
  },
  {
    id: "pricing",
    label: "Codziennie",
    title: "Wyceny z aktualną ceną produktów",
    description:
      "Tworząc usługę, widzisz koszt zużytych produktów w czasie rzeczywistym.",
    icon: FileSpreadsheet,
    size: "sm",
    bullets: [
      "Aktualna cena produktów w wycenie",
      "Marża widoczna od razu",
      "Historia cen — bez liczenia w głowie",
    ],
  },
  {
    id: "team",
    label: "Codziennie",
    title: "Grafik, prowizje, urlopy",
    description: "Zarządzanie personelem w jednym panelu. Koniec z Excelem.",
    icon: CalendarCheck,
    size: "sm",
    bullets: [
      "Grafik z widokiem zespołu",
      "Prowizje liczone automatycznie",
      "Wnioski urlopowe w jednym miejscu",
    ],
  },
];

// ─── Ornament: AI prep — stack of history cards + foreground notification z typewriter ───
const AI_SUMMARIES = [
  {
    name: "Reksio, 4 lata",
    lines: [
      "Przewlekłe zapalenie skóry — terapia w toku.",
      "Ostatnia wizyta: 3 tyg. temu.",
    ],
    history: [
      "06 maja 2024 · Wizyta kontrolna · Reksio",
      "12 marca 2024 · Szczepienie · Reksio",
      "18 stycznia 2024 · Konsultacja · Reksio",
      "04 listopada 2023 · Badanie krwi · Reksio",
    ],
  },
  {
    name: "Mruczek, 8 lat",
    lines: [
      "Cukrzyca typ I — kontrola glukozy.",
      "Insulina 2× dziennie.",
    ],
    history: [
      "20 kwietnia 2024 · Pomiar glukozy · Mruczek",
      "11 lutego 2024 · Wizyta kontrolna · Mruczek",
      "29 grudnia 2023 · Badanie moczu · Mruczek",
      "15 października 2023 · Diagnoza · Mruczek",
    ],
  },
  {
    name: "Bella, 2 lata",
    lines: [
      "Kontrola pooperacyjna po sterylizacji.",
      "Bez powikłań.",
    ],
    history: [
      "02 maja 2024 · Sterylizacja · Bella",
      "28 kwietnia 2024 · Konsultacja przedoper. · Bella",
      "10 marca 2024 · Szczepienie · Bella",
      "22 stycznia 2024 · Pierwsza wizyta · Bella",
    ],
  },
];

function AIPrepOrnament() {
  const [index, setIndex] = useState(0);
  const [shownLine1, setShownLine1] = useState("");
  const [shownLine2, setShownLine2] = useState("");
  const [phase, setPhase] = useState<"appearing" | "typing1" | "typing2" | "hold" | "fadeout">("appearing");

  useEffect(() => {
    const summary = AI_SUMMARIES[index];

    if (phase === "appearing") {
      const t = setTimeout(() => setPhase("typing1"), 800);
      return () => clearTimeout(t);
    }
    if (phase === "typing1") {
      if (shownLine1.length < summary.lines[0].length) {
        const t = setTimeout(
          () => setShownLine1(summary.lines[0].slice(0, shownLine1.length + 1)),
          28,
        );
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase("typing2"), 200);
      return () => clearTimeout(t);
    }
    if (phase === "typing2") {
      if (shownLine2.length < summary.lines[1].length) {
        const t = setTimeout(
          () => setShownLine2(summary.lines[1].slice(0, shownLine2.length + 1)),
          28,
        );
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase("hold"), 100);
      return () => clearTimeout(t);
    }
    if (phase === "hold") {
      const t = setTimeout(() => setPhase("fadeout"), 2800);
      return () => clearTimeout(t);
    }
    if (phase === "fadeout") {
      const t = setTimeout(() => {
        setShownLine1("");
        setShownLine2("");
        setIndex((i) => (i + 1) % AI_SUMMARIES.length);
        setPhase("appearing");
      }, 600);
      return () => clearTimeout(t);
    }
  }, [phase, shownLine1, shownLine2, index]);

  const summary = AI_SUMMARIES[index];
  const notificationVisible = phase === "typing1" || phase === "typing2" || phase === "hold";

  return (
    <div
      className="absolute inset-0 pointer-events-none px-4 pt-3 pb-4 flex flex-col"
      aria-hidden
    >
      {/* AI notification — zarezerwowana strefa u góry (stała wysokość, opacity przełącza widoczność) */}
      <div className="flex justify-center" style={{ minHeight: 96 }}>
        <motion.div
          className="w-[230px] max-w-full"
          initial={{ opacity: 0, y: -8 }}
          animate={{
            opacity: notificationVisible ? 1 : 0,
            y: notificationVisible ? 0 : -8,
          }}
          transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
        >
          <div className="rounded-xl bg-card border border-border/60 shadow-lg shadow-primary/10 p-3 text-[10px] leading-relaxed">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <div className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                </div>
                <span className="font-semibold text-primary uppercase tracking-wider text-[8px]">
                  Podsumowanie AI
                </span>
              </div>
              <span className="text-[8px] font-medium text-muted-foreground">
                {summary.name}
              </span>
            </div>
            <p className="text-foreground/80 min-h-[28px]">
              {shownLine1}
              {phase === "typing2" || phase === "hold" || phase === "fadeout" ? (
                <>
                  <br />
                  {shownLine2}
                </>
              ) : null}
              {(phase === "typing1" || phase === "typing2") && (
                <motion.span
                  className="inline-block w-[2px] h-[9px] bg-primary ml-0.5 align-middle"
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.9, repeat: Infinity }}
                />
              )}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Historia pacjenta — flex-1 wypełnia resztę poniżej notification */}
      <motion.div
        key={`history-${index}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex-1 mt-3 flex flex-col gap-1.5 justify-end"
      >
        <div className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-0.5">
          Historia pacjenta
        </div>
        {summary.history.map((entry, i) => (
          <div
            key={`${index}-${i}`}
            className="flex items-center gap-2 text-[9px] text-muted-foreground truncate"
            style={{ opacity: [0.7, 0.5, 0.32, 0.18][i] }}
          >
            <span
              className="w-1 h-1 rounded-full bg-muted-foreground/50 flex-shrink-0"
              aria-hidden
            />
            <span className="truncate">{entry}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// ─── Ornament: All-included — SVG flow diagram z biegającymi kropkami ───
// ─── Ornament: All-included — mockup okna z 4 features WLICZONE ───
// 5 features „wliczone w cenę" — odwzorowuje hasło „Wszystko w jednej cenie, bez dopłat"
export const INCLUDED_FEATURES = [
  {
    label: "Aktualizacje systemu",
    desc: "wszystkie wersje, automatycznie",
  },
  {
    label: "Integracje z laboratoriami",
    desc: "Vetina Bridge — bez dopłat",
  },
  {
    label: "System rezerwacji online",
    desc: "alternatywa dla Wettermin",
  },
  {
    label: "Migracja danych",
    desc: "z dowolnego systemu",
  },
  {
    label: "Aplikacja mobilna dla klientów",
    desc: "alternatywa dla Vetineo",
  },
];

function AllIncludedOrnament() {
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    const STAGGER = 600;
    const PAUSE_AFTER_FULL = 3000;
    const RESET_DELAY = 500;

    if (revealedCount < INCLUDED_FEATURES.length) {
      const t = setTimeout(() => setRevealedCount((c) => c + 1), STAGGER);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      setTimeout(() => setRevealedCount(0), RESET_DELAY);
    }, PAUSE_AFTER_FULL);
    return () => clearTimeout(t);
  }, [revealedCount]);

  return (
    <div
      className="absolute inset-0 pointer-events-none flex items-start md:items-stretch justify-center pt-1 md:pt-0 md:pb-3 px-6 md:px-8"
      aria-hidden
    >
      <div className="w-full max-w-[440px] flex flex-col md:h-full">
        {INCLUDED_FEATURES.map((feature, i) => {
          const isRevealed = i < revealedCount;
          return (
            <motion.div
              key={feature.label}
              initial={false}
              animate={{ opacity: isRevealed ? 1 : 0.2 }}
              transition={{ duration: 0.4 }}
              className="flex items-center justify-between gap-3 py-3 md:py-0 md:flex-1 border-b border-border/40 last:border-b-0"
            >
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm md:text-base font-semibold text-foreground leading-tight">
                  {feature.label}
                </span>
                <span className="text-xs text-muted-foreground leading-tight mt-1">
                  {feature.desc}
                </span>
              </div>
              <motion.div
                initial={false}
                animate={{
                  opacity: isRevealed ? 1 : 0,
                  scale: isRevealed ? 1 : 0.7,
                  x: isRevealed ? 0 : 8,
                }}
                transition={{ duration: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[10px] font-bold text-emerald-700 flex-shrink-0"
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <path
                    d="M2.5 6.5L5 9L9.5 3.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                WLICZONE
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Ornament: Inventory — siatka + scan beam + counter ───
// Karta 3 — Inwentaryzacja: 5×4 grid produktów + counter pill + scan line
// Komórki zmieniają stan z „do zliczenia" na „zliczone" gdy scan line je przekracza.

// Karta 3 — Inwentaryzacja: lista realnych produktów weterynaryjnych z checkmarkami
// Pokazuje konkretną mechanikę: stan systemowy + input zliczono + różnica auto-liczona
// Wzorowane na realnym UI inwentaryzacji w Vetinie (z poprawkami żeby zmieścić się w tile)
type InventoryRow = {
  name: string;
  lot: string;
  systemValue: string;
  systemNum: number;
  countedTarget: number;
  unit: string;
};

const INVENTORY_ROWS: InventoryRow[] = [
  { name: "Cefalexin 250 mg", lot: "CEF-25A", systemValue: "47", systemNum: 47, countedTarget: 47, unit: "szt" },
  { name: "Frontline Combo", lot: "FRO-25B", systemValue: "23", systemNum: 23, countedTarget: 25, unit: "amp" },
  { name: "Maropitant inj.", lot: "MAR-25C", systemValue: "12", systemNum: 12, countedTarget: 11, unit: "fiol" },
  { name: "Bravecto 500 mg", lot: "BRA-25D", systemValue: "18", systemNum: 18, countedTarget: 18, unit: "tabl" },
];

function diffLabel(target: number, system: number): { text: string; tone: "match" | "over" | "under" } {
  const diff = target - system;
  if (diff === 0) return { text: "✓", tone: "match" };
  if (diff > 0) return { text: `+${diff}`, tone: "over" };
  return { text: `${diff}`, tone: "under" };
}

function InventoryRowItem({
  row,
  state,
}: {
  row: InventoryRow;
  state: "pending" | "active" | "done";
}) {
  // Counter animuje się tylko gdy state === "active". Pozostałe stany derive się z props.
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    if (state !== "active") return;
    const start = performance.now();
    const DURATION = 550;
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / DURATION, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedValue(Math.round(row.countedTarget * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state, row.countedTarget]);

  const displayed =
    state === "pending"
      ? 0
      : state === "done"
        ? row.countedTarget
        : animatedValue;

  const showResult = state === "done";
  const diff = diffLabel(row.countedTarget, row.systemNum);

  // Background tinting based on state
  let bgClass = "bg-card";
  if (state === "active") bgClass = "bg-amber-50/70";
  else if (state === "done") {
    if (diff.tone === "match") bgClass = "bg-emerald-50/60";
    else bgClass = "bg-amber-50/60";
  }

  const diffColor =
    diff.tone === "match"
      ? "text-emerald-700"
      : diff.tone === "over"
        ? "text-emerald-700"
        : "text-orange-700";

  return (
    <motion.div
      initial={false}
      animate={{ opacity: state === "pending" ? 0.45 : 1 }}
      transition={{ duration: 0.3 }}
      className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 px-2 py-1 border-b border-border/20 last:border-b-0 transition-colors ${bgClass}`}
    >
      {/* Nazwa + LOT */}
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] font-medium text-foreground truncate leading-tight">
          {row.name}
        </span>
        <span className="text-[8px] text-muted-foreground leading-tight mt-0.5">
          {row.lot}
        </span>
      </div>

      {/* Stan syst. */}
      <div className="text-[9px] tabular-nums text-muted-foreground flex-shrink-0 text-right">
        <div className="text-[7px] uppercase text-muted-foreground/60 leading-none mb-0.5">
          Syst.
        </div>
        <div className="leading-none">{row.systemValue} {row.unit}</div>
      </div>

      {/* Input „Zliczono" — animowany counter z ramką */}
      <div className="flex-shrink-0 text-right">
        <div className="text-[7px] uppercase text-muted-foreground/60 leading-none mb-0.5">
          Zliczono
        </div>
        <div
          className={`inline-flex items-center justify-end min-w-[36px] px-1.5 py-0.5 rounded border text-[9px] font-semibold tabular-nums leading-none ${
            state === "pending"
              ? "border-border/50 text-muted-foreground/50 bg-card"
              : "border-primary/30 text-foreground bg-card"
          }`}
        >
          {state === "pending" ? "—" : displayed}
        </div>
      </div>

      {/* Różnica */}
      <div className="flex-shrink-0 w-[28px] text-right">
        <div className="text-[7px] uppercase text-muted-foreground/60 leading-none mb-0.5">
          Różn.
        </div>
        <motion.div
          key={`${row.name}-${state}`}
          initial={state === "done" ? { opacity: 0, scale: 0.8 } : false}
          animate={{ opacity: showResult ? 1 : 0, scale: showResult ? 1 : 0.8 }}
          transition={{ duration: 0.25, delay: showResult ? 0.05 : 0 }}
          className={`text-[10px] font-bold tabular-nums leading-none ${diffColor}`}
        >
          {showResult ? diff.text : "—"}
        </motion.div>
      </div>
    </motion.div>
  );
}

function InventoryOrnament() {
  // 0 = nic, 1 = aktywny pierwszy, 2 = aktywny drugi (pierwszy done), ..., N+1 = wszystkie done, N+2 = pauza, reset
  const [step, setStep] = useState(0);
  const N = INVENTORY_ROWS.length;

  useEffect(() => {
    const ACTIVE_DURATION = 900; // każda pozycja: 900ms aktywna
    const PAUSE_AFTER = 2400;
    const RESET_DELAY = 400;

    if (step <= N) {
      const t = setTimeout(() => setStep((s) => s + 1), ACTIVE_DURATION);
      return () => clearTimeout(t);
    }
    // step === N + 1: pauza, potem reset
    const t = setTimeout(() => {
      setTimeout(() => setStep(0), RESET_DELAY);
    }, PAUSE_AFTER);
    return () => clearTimeout(t);
  }, [step, N]);

  // Liczba ukończonych = step - 1 (clamp do 0..N)
  const doneCount = Math.max(0, Math.min(step - 1, N));
  const counter = 1 + doneCount; // licznik mockup (zliczono X / 31)
  const progress = doneCount / N;

  return (
    <div
      className="absolute inset-0 pointer-events-none flex items-center justify-center px-3"
      aria-hidden
    >
      <div className="relative w-full max-w-[280px]">
        <div className="rounded-xl bg-card border border-border/60 shadow-lg overflow-hidden">
          {/* Header — counter + progress bar */}
          <div className="px-3 py-1.5 border-b border-border/40">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[9px] font-semibold tabular-nums text-foreground">
                Zliczono <span className="text-primary">{counter}</span>
                <span className="text-muted-foreground"> / 31 pozycji</span>
              </div>
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
              </span>
            </div>
            {/* Progress bar */}
            <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
              />
            </div>
          </div>

          {/* Tabela */}
          <div className="flex flex-col">
            {INVENTORY_ROWS.map((row, i) => {
              let rowState: "pending" | "active" | "done" = "pending";
              if (i < step - 1) rowState = "done";
              else if (i === step - 1) rowState = "active";

              return <InventoryRowItem key={row.name} row={row} state={rowState} />;
            })}
          </div>

          {/* Footer */}
          <div className="px-3 py-1 border-t border-border/40 bg-muted/20 flex items-center justify-between">
            <span className="text-[8px] text-muted-foreground">
              Wizyty toczą się normalnie
            </span>
            <span className="text-[8px] font-medium text-emerald-700">
              ● Live
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function VisualOrnament({ id }: { id: string }) {
  if (id === "all-included") return <AllIncludedOrnament />;
  if (id === "ai-prep") return <AIPrepOrnament />;
  if (id === "inventory") return <InventoryOrnament />;
  if (id === "any-device") return <VisualDevices />;
  if (id === "pricing") return <VisualPricing />;
  if (id === "team") return <VisualTeam />;
  return null;
}

function BentoCard({
  tile,
  onOpen,
}: {
  tile: BentoTile;
  onOpen: () => void;
}) {
  const isLg = tile.size === "lg";
  const isMd = tile.size === "md";

  const minHeightClass = isLg
    ? "min-h-[440px] md:min-h-[660px]"
    : isMd
      ? "min-h-[340px] md:min-h-[320px]"
      : "min-h-[360px] md:min-h-[320px]";

  return (
    <motion.button
      onClick={onOpen}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      className={`group relative overflow-hidden rounded-2xl border border-border bg-card text-left hover:border-primary/30 hover:shadow-lg transition-all w-full h-full flex flex-col ${minHeightClass}`}
    >
      {/* Bazowy gradient (ledwie widoczny) na całym kafelku */}
      {tile.accent === "primary" && (
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/[0.03] via-transparent to-transparent" />
      )}

      {/* Always-visible expand affordance (top-right) — Stripe-style „kliknij" */}
      <div
        className="absolute top-4 right-4 md:top-5 md:right-5 h-7 w-7 rounded-md bg-muted/60 group-hover:bg-primary/15 group-hover:text-primary text-muted-foreground transition-colors flex items-center justify-center pointer-events-none z-10"
        aria-hidden
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M13.75 6.75L10.25 6.75L10.25 5L15.5 5L15.5 10.25L13.75 10.25L13.75 6.75Z" />
          <path d="M6.75 10.25L5 10.25L5 15.5L10.25 15.5L10.25 13.75L6.75 13.75L6.75 10.25Z" />
        </svg>
      </div>

      {/* HEADER ZONE — tylko tytuł, własna strefa u góry */}
      <div className="px-6 md:px-7 pt-6 md:pt-7 pb-3 pr-14">
        <h3
          className={`font-semibold tracking-tight ${
            isLg
              ? "text-xl md:text-2xl max-w-[24rem] leading-tight"
              : isMd
                ? "text-base md:text-lg max-w-[16rem] leading-snug"
                : "text-sm md:text-base max-w-[14rem] leading-snug"
          }`}
        >
          {tile.title}
        </h3>
      </div>

      {/* GRAPHIC ZONE — flex-1, ornaments pozycjonują się względem tej strefy */}
      <div className="relative flex-1 overflow-hidden">
        {/* Pastelowy gradient w strefie graficznej (lg only) */}
        {isLg && (
          <div
            className="absolute inset-0 -z-10 pointer-events-none"
            style={{
              background:
                "radial-gradient(80% 70% at 50% 80%, color-mix(in srgb, var(--primary) 14%, transparent) 0%, transparent 70%)",
            }}
          />
        )}
        <VisualOrnament id={tile.id} />
      </div>
    </motion.button>
  );
}

function TileModal({
  tile,
  onClose,
}: {
  tile: BentoTile | null;
  onClose: () => void;
}) {
  return (
    <AnimatePresence>
      {tile && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:max-w-2xl z-50 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              aria-label="Zamknij"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="p-8 md:p-10 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <tile.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 text-[10px] uppercase tracking-wide">
                  {tile.label}
                </Badge>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                {tile.title}
              </h3>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                {tile.description}
              </p>

              <ul className="mt-7 space-y-3">
                {tile.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">{bullet}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-border/40">
                <DemoButton
                  source={`bento-modal-${tile.id}`}
                  size="lg"
                  onBeforeOpen={onClose}
                  className="w-full text-base shadow-lg shadow-primary/20"
                >
                  Zostaw numer — oddzwonimy
                </DemoButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function BentoStripe() {
  const [openTile, setOpenTile] = useState<BentoTile | null>(null);

  return (
    <section id="funkcje" className="py-16 md:py-24 bg-muted/30 border-y border-border/40">
      <Container>
        <AnimateOnScroll className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="text-sm font-medium tracking-wide text-primary uppercase mb-3">
            Czego nie znajdziesz u konkurencji
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-balance">
            Funkcje, które ma Vetina
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Nie kopiujemy starych systemów. Budujemy funkcje, których w polskiej
            weterynarii jeszcze nie było.
          </p>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 auto-rows-[minmax(180px,auto)]">
          {TILES.map((tile, i) => (
            <AnimateOnScroll key={tile.id} delay={i * 0.05} className={
              tile.size === "lg" ? "md:col-span-2 md:row-span-2" :
              tile.size === "md" ? "md:col-span-1 md:row-span-1" :
              "md:col-span-1"
            }>
              <BentoCard tile={tile} onOpen={() => setOpenTile(tile)} />
            </AnimateOnScroll>
          ))}
        </div>

      </Container>

      <TileModal tile={openTile} onClose={() => setOpenTile(null)} />
    </section>
  );
}
